import * as React from "react";
import { format } from "date-fns";
import { ka, enUS } from "date-fns/locale";
import { CalendarIcon, Clock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";

interface DeliveryDatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  disabled?: boolean;
  className?: string;
  allowPast?: boolean;
  includeTime?: boolean;
}

export function DeliveryDatePicker({
  value,
  onChange,
  disabled = false,
  className,
  allowPast = false,
  includeTime = false,
}: DeliveryDatePickerProps) {
  const { t, language } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [selectedTime, setSelectedTime] = React.useState<string>("12:00");

  // Parse the date string to Date object
  const selectedDate = React.useMemo(() => {
    if (!value) return undefined;
    // Handle both date-only and datetime formats
    const dateStr = value.includes("T") ? value.split("T")[0] : value;
    const parsed = new Date(dateStr + "T00:00:00");
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }, [value]);

  // Extract time if includeTime and value has time component
  React.useEffect(() => {
    if (includeTime && value && value.includes("T")) {
      const timePart = value.split("T")[1];
      if (timePart) {
        setSelectedTime(timePart.substring(0, 5));
      }
    }
  }, [value, includeTime]);

  const handleSelect = React.useCallback(
    (date: Date | undefined) => {
      if (date) {
        // Format as YYYY-MM-DD for database storage
        const formatted = format(date, "yyyy-MM-dd");
        if (includeTime) {
          onChange(`${formatted}T${selectedTime}`);
        } else {
          onChange(formatted);
        }
      }
      if (!includeTime) {
        setOpen(false);
      }
    },
    [onChange, includeTime, selectedTime]
  );

  const handleTimeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = e.target.value;
      setSelectedTime(newTime);
      if (value) {
        const dateStr = value.includes("T") ? value.split("T")[0] : value;
        onChange(`${dateStr}T${newTime}`);
      }
    },
    [onChange, value]
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null);
      setSelectedTime("12:00");
    },
    [onChange]
  );

  const handleDone = React.useCallback(() => {
    setOpen(false);
  }, []);

  // Determine disabled dates (past dates by default)
  const disabledDays = React.useMemo(() => {
    if (allowPast) return undefined;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { before: today };
  }, [allowPast]);

  // Format display date based on language
  const displayDate = React.useMemo(() => {
    if (!selectedDate) return null;
    const dateFormat = language === "ka" ? "d MMM yyyy" : "MMM d, yyyy";
    const formattedDate = format(selectedDate, dateFormat);
    if (includeTime && value?.includes("T")) {
      return `${formattedDate} ${selectedTime}`;
    }
    return formattedDate;
  }, [selectedDate, language, includeTime, value, selectedTime]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal bg-background hover:bg-muted/50",
            "focus-visible:ring-1 focus-visible:ring-primary/40 focus-visible:ring-offset-0 focus-visible:outline-none",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
          {displayDate || (
            <span>{t("orders.placeholders.selectDeliveryDate") || "Select delivery date"}</span>
          )}
          {value && !disabled && (
            <button
              type="button"
              className="ml-auto p-1 rounded-sm opacity-60 hover:opacity-100 hover:bg-muted transition-all"
              onClick={handleClear}
              aria-label={t("common.cancel") || "Clear date"}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={disabledDays}
          locale={language === "ka" ? ka : enUS}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
        {includeTime && (
          <div className="border-t border-border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                {t("orders.labels.deliveryTime") || "Delivery Time"}
              </span>
            </div>
            <Input
              type="time"
              value={selectedTime}
              onChange={handleTimeChange}
              className="w-full"
            />
            <Button 
              type="button"
              size="sm" 
              className="w-full" 
              onClick={handleDone}
            >
              {t("common.save") || "Done"}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
